import classNames from 'classnames';
import RCNotification from 'rc-notification';
import { NotificationInstance as RCNotificationInstance } from 'rc-notification/lib/Notification';
import React, { ReactNode } from 'react';

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

import s from './Notification.module.css';

type NotificationPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight';
const typeToIcon = {
  success: CheckCircleOutlined,
  info: InfoCircleOutlined,
  error: CloseCircleOutlined,
  warning: ExclamationCircleOutlined,
  loading: LoadingOutlined,
};
const defaultDuration = 4.5;
const defaultTop = 16;
const defaultBottom = 16;
const defaultPlacement: NotificationPlacement = 'topRight';

const notificationInstance: {
  [key: string]: Promise<RCNotificationInstance>;
} = {};

type IconType = 'success' | 'info' | 'error' | 'warning' | 'loading';

interface NotificationOptions {
  title?: string;
  message?: ReactNode;
  icon?: ReactNode;
  duration?: number | null;
  readonly type?: IconType;
  key?: string;
  placement?: NotificationPlacement;
  htmlMessage?: boolean;
  closable?: boolean;
}

function getPlacementStyle(placement: NotificationPlacement) {
  let style;
  const top = defaultTop;
  const bottom = defaultBottom;

  switch (placement) {
    case 'top':
      style = {
        left: 0,
        right: 0,
        top,
        bottom: 'auto',
      };
      break;
    case 'topLeft':
      style = {
        left: 0,
        top,
        bottom: 'auto',
      };
      break;
    case 'topRight':
      style = {
        right: 0,
        top,
        bottom: 'auto',
      };
      break;
    case 'bottom':
      style = {
        left: 0,
        right: 0,
        top: 'auto',
        bottom,
      };
      break;
    case 'bottomLeft':
      style = {
        left: 0,
        top: 'auto',
        bottom,
      };
      break;
    default:
      style = {
        right: 0,
        top: 'auto',
        bottom,
      };
      break;
  }
  return style;
}

function getRCNoticeProps(options: NotificationOptions, prefixCls: string) {
  const {
    duration: durationArg,
    icon,
    type,
    message,
    key,
    title,
    htmlMessage,
    closable = true,
  } = options;

  const duration = durationArg === undefined ? defaultDuration : durationArg;

  let iconNode: React.ReactNode = null;
  if (icon) {
    iconNode = <span className={`${prefixCls}-icon ${s.icon}`}>{options.icon}</span>;
  } else if (type) {
    iconNode = React.createElement(typeToIcon[type] || null, {
      className: `${prefixCls}-icon ${prefixCls}-icon-${type} ${s.icon}`,
    });
  }

  const closeIconToRender = (
    <span className={`${prefixCls}-close-x`}>
      <CloseOutlined className={`${prefixCls}-close-icon`} />
    </span>
  );

  const autoMarginTag =
    !title && iconNode ? <span className={`${prefixCls}-message-single-line-auto-margin`} /> : null;

  return {
    content: (
      <div
        className={classNames(
          s.root,
          { [`${prefixCls}-with-icon`]: !!iconNode },
          s[type || 'info'],
        )}
        role="alert"
      >
        {iconNode}
        <div className={s.content}>
          <div className={s.title}>
            {autoMarginTag}
            {title}
          </div>
          {message &&
            (htmlMessage ? (
              <div className={s.message} dangerouslySetInnerHTML={{ __html: message || '' }} />
            ) : (
              <div className={s.message}>{message}</div>
            ))}
        </div>
      </div>
    ),
    duration,
    closable: closable,
    closeIcon: closeIconToRender,
    key,
  };
}

function getNotificationInstance(
  options: NotificationOptions,
  callback: (info: { prefixCls: string; instance: RCNotificationInstance }) => void,
) {
  const { placement = defaultPlacement } = options;
  const prefixCls = 'citi-notification';
  // const iconPrefixCls = 'citi-notification-icon';

  const cacheKey = `${prefixCls}-${placement}`;
  const cacheInstance = notificationInstance[cacheKey];
  if (cacheInstance) {
    Promise.resolve(cacheInstance).then((instance) => {
      callback({ prefixCls: `${prefixCls}-notice`, instance });
    });

    return;
  }

  const notificationClass = `${prefixCls}-${placement}`;

  notificationInstance[cacheKey] = new Promise((resolve) => {
    RCNotification.newInstance(
      {
        prefixCls,
        className: notificationClass,
        style: getPlacementStyle(placement),
      },
      (notification) => {
        resolve(notification);
        callback({
          prefixCls: `${prefixCls}-notice`,
          instance: notification,
        });
      },
    );
  });
}

const notice = (options: NotificationOptions) => {
  getNotificationInstance(options, ({ prefixCls, instance }) => {
    instance.notice(getRCNoticeProps(options, prefixCls));
  });
};

const Notification = {
  open: notice,
  close(key: string) {
    Object.keys(notificationInstance).forEach((cacheKey) =>
      Promise.resolve(notificationInstance[cacheKey]).then((instance) => {
        instance.removeNotice(key);
      }),
    );
  },
  destroy() {
    Object.keys(notificationInstance).forEach((cacheKey) => {
      Promise.resolve(notificationInstance[cacheKey]).then((instance) => {
        instance.destroy();
      });
      delete notificationInstance[cacheKey]; // lgtm[js/missing-await]
    });
  },
  success: (options: NotificationOptions) =>
    Notification.open({
      ...options,
      type: 'success',
    }),
  info: (options: NotificationOptions) =>
    Notification.open({
      ...options,
      type: 'info',
    }),
  warning: (options: NotificationOptions) =>
    Notification.open({
      ...options,
      type: 'warning',
    }),
  error: (options: NotificationOptions) =>
    Notification.open({
      ...options,
      type: 'error',
    }),
};

export interface NotificationInstance {
  success(options: NotificationOptions): void;

  error(options: NotificationOptions): void;

  info(options: NotificationOptions): void;

  warning(options: NotificationOptions): void;

  open(options: NotificationOptions): void;
}

export interface NotificationApi extends NotificationInstance {
  warn(options: NotificationOptions): void;

  close(key: string): void;

  destroy(): void;
}

export default Notification as NotificationApi;
